import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Gauge, ShieldAlert, Sparkles, TrendingUp, Bird, HeartPulse, HardHat, Wallet,
  PackageMinus, Cloud, Bell, Users, Landmark, Clock, ArrowUpRight, AlertTriangle,
  ClipboardCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePermissions } from "@/lib/permissions";
import { PageHero, Section, Badge, Button, EmptyState } from "@/components/premium/UI";
import { Pattern } from "@/components/premium/Patterns";

const money = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_TONE = { excellent: "success", good: "primary", attention: "pending", critical: "critical", insufficient: "neutral" };
const PRIORITY_TONE = { critical: "critical", high: "pending", medium: "primary", low: "neutral" };

function ScoreGauge({ score, size = 180 }) {
  const s = Math.max(0, Math.min(100, score ?? 0));
  const r = size / 2 - 12;
  const circ = 2 * Math.PI * r;
  const dash = circ * (s / 100);
  const colour = s >= 85 ? "#1E8E5A" : s >= 70 ? "#0B2E59" : s >= 50 ? "#F28C28" : "#DC2626";
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size/2} cy={size/2} r={r} stroke="hsl(var(--muted))" strokeWidth="10" fill="none" />
      <circle
        cx={size/2} cy={size/2} r={r}
        stroke={colour} strokeWidth="10" fill="none"
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: "stroke-dasharray 800ms cubic-bezier(0.4,0,0.2,1)" }}
      />
      <text x="50%" y="47%" textAnchor="middle" className="font-heading" fontSize="34" fontWeight="800" fill="hsl(var(--foreground))">{score == null ? "—" : Math.round(score)}</text>
      <text x="50%" y="62%" textAnchor="middle" fontSize="11" fill="hsl(var(--muted-foreground))" style={{textTransform: "uppercase", letterSpacing: "0.15em"}}>score</text>
    </svg>
  );
}

function KpiTile({ label, value, hint, icon: Icon, tone = "primary", to, testid }) {
  const inner = (
    <div data-testid={testid} className="kpi-card group cursor-pointer h-full">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="eyebrow truncate">{label}</div>
          <div className="mt-1.5 font-heading text-2xl font-bold tracking-tight">{value}</div>
          {hint && <div className="subtle mt-1 truncate">{hint}</div>}
        </div>
        {Icon && (
          <div className={`shrink-0 h-9 w-9 grid place-items-center rounded-lg ${tone === "success" ? "bg-success/10 text-success" : tone === "danger" ? "bg-destructive/10 text-destructive" : tone === "accent" ? "bg-accent/10 text-accent" : "bg-primary/10 text-primary"}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
      {to && <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function OperatingCentre() {
  const { user } = useAuth();
  const { roleName, isExecutive } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    try { const r = await api.get("/operating-centre"); setData(r.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-40 rounded-3xl bg-muted/40" />
        <div className="grid grid-cols-4 gap-4">{[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-24 rounded-2xl bg-muted/40" />)}</div>
      </div>
    );
  }

  const k = data?.kpis || {};

  return (
    <div className="space-y-6" data-testid="operating-centre-page">
      <PageHero
        eyebrow="Executive Operating Centre"
        title="Mission control"
        subtitle={`${data?.property?.name || "Thom Homestead"} · Signed in as ${roleName || user?.role}${isExecutive ? " (Executive)" : ""} · Live snapshot updating every 60 seconds`}
        moduleKind="situation"
        actions={
          <>
            <Badge variant={data?.risk_index > 60 ? "critical" : data?.risk_index > 30 ? "pending" : "healthy"}>
              <ShieldAlert className="w-3 h-3" /> Risk index {data?.risk_index ?? "—"}%
            </Badge>
          </>
        }
      />

      {/* Hero score + KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
        <Section className="relative overflow-hidden flex items-center justify-center py-6" title="">
          <Pattern kind="situation" />
          <div className="relative text-center">
            <div className="eyebrow mb-2">Homestead score</div>
            <ScoreGauge score={data?.overall_score} />
            <div className="mt-2 text-xs text-muted-foreground">{data?.scorecards?.filter(s => s.score != null).length || 0} of {data?.scorecards?.length || 0} domains scored</div>
          </div>
        </Section>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {k.cash_position != null && <KpiTile testid="oc-kpi-cash" label="Cash position" value={money(k.cash_position)} hint={`Budget utilisation ${k.budget_utilisation ?? "—"}%`} icon={Landmark} tone={k.cash_position < 0 ? "danger" : "success"} to="/finance" />}
          {k.construction && <KpiTile testid="oc-kpi-construction" label="Construction" value={`${k.construction.completion_pct ?? "—"}%`} hint={`${k.construction.project} · ${k.construction.phase}`} icon={HardHat} tone="accent" to="/projects" />}
          {k.pending_approvals != null && <KpiTile testid="oc-kpi-approvals" label="Pending approvals" value={k.pending_approvals} hint="Daily project submissions" icon={ClipboardCheck} tone={k.pending_approvals > 0 ? "accent" : "primary"} to="/projects" />}
          {k.birds != null && <KpiTile testid="oc-kpi-birds" label="Flock" value={k.birds} hint={`Mortality ${k.mortality_pct ?? 0}%`} icon={Bird} tone="accent" to="/poultry" />}
          {k.health && <KpiTile testid="oc-kpi-health" label="Health readings today" value={k.health.readings_today} hint={`Patient: ${k.health.patient}`} icon={HeartPulse} tone="success" to="/health" />}
          {k.inventory && <KpiTile testid="oc-kpi-inventory" label="Low stock" value={k.inventory.low_stock} hint={`${k.inventory.total} items tracked`} icon={PackageMinus} tone={k.inventory.low_stock > 0 ? "danger" : "success"} to="/inventory" />}
          {k.staff_active != null && <KpiTile testid="oc-kpi-staff" label="Active staff" value={k.staff_active} hint={`${k.staff_on_leave ?? 0} on leave`} icon={Users} tone="primary" to="/hr" />}
          {k.weather && <KpiTile testid="oc-kpi-weather" label="Weather" value={`${k.weather.temperature_c ?? "—"}°C`} hint={`${k.weather.description || "—"}${k.weather.alerts ? ` · ${k.weather.alerts} alert(s)` : ""}`} icon={Cloud} tone={k.weather.alerts > 0 ? "accent" : "primary"} />}
          <KpiTile testid="oc-kpi-notifications" label="Unread notifications" value={k.unread_notifications ?? 0} hint="Across all channels" icon={Bell} tone={k.unread_notifications > 0 ? "accent" : "primary"} to="/notifications" />
        </div>
      </div>

      {/* Scorecards */}
      <Section title="Executive performance scorecards" description="Weighted domain scores from the Situation Room engine." actions={<Link to="/situation"><Button variant="ghost" size="sm">Open Situation Room</Button></Link>}>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {(data?.scorecards || []).map(s => (
            <div key={s.key} data-testid={`oc-scorecard-${s.key}`} className="rounded-2xl border border-border p-4 bg-card">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">{s.label}</div>
                <Badge variant={STATUS_TONE[s.status] || "neutral"}>{s.status}</Badge>
              </div>
              <div className="font-heading text-3xl font-bold">{s.score == null ? "—" : s.score}</div>
              <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-gradient-to-r from-primary to-accent transition-all" style={{width: `${Math.max(0, Math.min(100, s.score ?? 0))}%`}} />
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground line-clamp-1">{s.detail}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Risks + Recommendations side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section title="Risk centre" description="Active high/critical risks the engine has flagged." actions={<Link to="/intelligence"><Button variant="ghost" size="sm">All insights</Button></Link>}>
          {(data?.risks || []).length === 0 ? <EmptyState icon={ShieldAlert} title="No active risks" description="All monitored risks are currently within acceptable range." /> :
            <ul className="space-y-2" data-testid="oc-risks">
              {data.risks.map(r => (
                <li key={r.id} className="rounded-xl border border-border p-3 bg-card">
                  <div className="flex items-center gap-2 mb-1.5">
                    <AlertTriangle className={`w-4 h-4 ${r.priority === "critical" ? "text-destructive" : "text-accent"}`} />
                    <Badge variant={PRIORITY_TONE[r.priority] || "neutral"}>{r.priority}</Badge>
                    <Badge variant="neutral">{r.module}</Badge>
                    <span className="ml-auto text-[10px] text-muted-foreground">confidence {r.confidence}%</span>
                  </div>
                  <div className="text-sm font-medium">{r.observation}</div>
                  <div className="text-xs text-muted-foreground mt-1">{r.recommendation}</div>
                </li>
              ))}
            </ul>
          }
        </Section>

        <Section title="AI recommendations & forecasts" description="Actionable next steps drawn from the intelligence engine." actions={<Link to="/knowledge"><Button variant="ghost" size="sm">Knowledge360</Button></Link>}>
          {(data?.recommendations || []).length === 0 ? <EmptyState icon={Sparkles} title="No recommendations right now" description="The engine has no proactive actions or forecasts to surface." /> :
            <ul className="space-y-2" data-testid="oc-recommendations">
              {data.recommendations.map(r => (
                <li key={r.id} className="rounded-xl border border-border p-3 bg-card">
                  <div className="flex items-center gap-2 mb-1.5">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <Badge variant="primary">{r.kind}</Badge>
                    <Badge variant="neutral">{r.module}</Badge>
                    <span className="ml-auto text-[10px] text-muted-foreground">confidence {r.confidence}%</span>
                  </div>
                  <div className="text-sm font-medium">{r.observation}</div>
                  <div className="text-xs mt-1">{r.recommendation}</div>
                </li>
              ))}
            </ul>
          }
        </Section>
      </div>

      {/* Executive Command Timeline + Decision Centre */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-5">
        <Section title="Executive command timeline" description="Every significant event across the homestead." actions={<Link to="/knowledge"><Button variant="ghost" size="sm">Full timeline</Button></Link>}>
          {(data?.timeline || []).length === 0 ? <EmptyState icon={Clock} title="No events yet" /> :
            <ol className="relative border-l border-border ml-2" data-testid="oc-timeline">
              {data.timeline.map(e => (
                <li key={e.id} className="ml-5 mb-3 relative">
                  <span className="absolute -left-[23px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary ring-4 ring-background" />
                  <div className="rounded-lg border border-border p-2.5 bg-card">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Badge variant="primary">{e.kind}</Badge>
                      <Badge variant="neutral">{e.module}</Badge>
                      <span className="ml-auto text-[10px] text-muted-foreground">{e.occurred_at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="text-sm font-medium line-clamp-1">{e.title}</div>
                    {e.summary && <div className="text-[11px] text-muted-foreground line-clamp-1">{e.summary}</div>}
                  </div>
                </li>
              ))}
            </ol>
          }
        </Section>

        <Section title="Executive decisions" description="Open & in-progress decisions requiring follow-through." actions={<Link to="/knowledge"><Button variant="ghost" size="sm">Decision register</Button></Link>}>
          {(data?.decisions_open || []).length === 0 ? <EmptyState icon={Landmark} title="No open decisions" description="Record executive decisions to build institutional memory." /> :
            <ul className="space-y-2" data-testid="oc-decisions">
              {data.decisions_open.map(d => (
                <li key={d.id} className="rounded-xl border border-border p-3 bg-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={d.status === "in_progress" ? "primary" : "pending"}>{d.status}</Badge>
                    <Badge variant="neutral">{d.module}</Badge>
                    {d.target_completion && <span className="ml-auto text-[10px] text-muted-foreground">→ {d.target_completion}</span>}
                  </div>
                  <div className="text-sm font-medium line-clamp-1">{d.title}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">Responsible: {d.responsible_person || "—"}</div>
                </li>
              ))}
            </ul>
          }
        </Section>
      </div>
    </div>
  );
}
